import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { withRouter, Link, useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { withTheme } from "@material-ui/styles";
import { useParams } from "react-router-dom";

import useSetCookies from "../hooks/useSetCookies";
import useForms from "../hooks/useForms";
import useYMMTLabels from "../hooks/useYMMTLabels";
import withFormikField from "../hoc/withFormikField";
import Page from "../layout/Page";
import VehicleInfoHeader from "../containers/VehicleInfoHeader";
import Accessories from "../containers/Accessories";
import ConditionRadio from "../components/ConditionRadio";
import OwnerOptions from "../components/OwnerOptions";
import ThousandNumberTextField from "../components/ThousandNumberTextField";
import useAccountSettings from "../hooks/useAccountSettings";
import { CallToActionButton } from "../components/Buttons";
import useTradeInState from "../hooks/useTradeInState";
import { vehicle } from "../data/vehicle";
import { fireVehicleIndicatedEvent } from "../utils/gtm-events";
import { FormLabel } from "@material-ui/core";

const FormStyled = styled(Form)`
  display: flex;
  flex-direction: column;
  max-width: 705px;
  margin: 10px auto;
  flex-grow: 1;
  padding: 10px;
`;

const FieldStyled = styled(Field)`
  && {
    margin: 20px 0;
  }
`;

const FormattedThousandNumberField = withTheme(styled(ThousandNumberTextField)`
  .MuiFormLabel-root {
    font-weight: 900;
    color: ${(props) => props.theme.palette.getContrastText("#FFF")};
  }
`);

const FormattedNumberField = withFormikField(FormattedThousandNumberField);

const ConditionRadioField = ({ field, ...props }) => (
  <ConditionRadio {...field} {...props} />
);

const ChangeButton = styled(CallToActionButton).attrs({
  variant: "contained",
  color: "gray",
})`
  && {
    color: #525252;
    font-weight: 400;
    padding: 5px 15px;
  }
`;

const SubmitButton = styled(CallToActionButton).attrs({
  type: "submit",
  variant: "contained",
  color: "primary",
})`
  width: 200px;
`;

const RequiredText = styled(Typography).attrs({
  variant: "subtitle2",
  color: "textSecondary",
})`
  && {
    margin-bottom: 20px;
  }
`;

export const SectionTitle = styled(FormLabel)`
  font-weight: 600 !important;
  margin-bottom: 15px;
  font-size: 1.1rem !important;
`;

const vehicleInfoSchema = Yup.object().shape({
  miles: Yup.number()
    .notOneOf([""])
    .required("Estimated mileage is required for trade report"),
  owner: Yup.string().required("Past owner is required"),
});

const VehicleInfoPage = ({ children }) => {
  const params = useParams();
  const history = useHistory();
  const [{ showBelowAverageValue, removeBranding, vehicleOfInterestRequired }] =
    useAccountSettings(params);
  const [trade, updateTrade] = useTradeInState();
  const [loading, setLoading] = useState(false);

  const { yearId, makeId, modelId, trimId, accountId } = params;
  useSetCookies(params);
  const { showInterestedVehicle } = useForms(accountId);
  const [[year, make, model, trim]] = useYMMTLabels(params);

  useEffect(() => {
    if (year && make && model && trim) {
      fireVehicleIndicatedEvent({
        leadId: trade.leadId,
        year,
        make,
        model,
        trim,
      });
    }
  }, [year, make, model, trim, trade.leadId]);

  const handleSubmit = async (values) => {
    setLoading(true);
    const { condition, miles, accessories, owner } = values;

    let accessoryIds;
    if (accessories) {
      accessoryIds = Object.values(accessories);
    }

    const info = {
      accessoryIds,
      condition,
      miles: miles,
      singleOwner: owner === "single",
    };

    try {
      const tradeVehicle = await vehicle({ ...trade, ...params, ...info });
      updateTrade({ ...tradeVehicle, accessoryIds });
      const tradeInValue = tradeVehicle.tradeInRanges[tradeVehicle.condition];

      if (tradeInValue <= 0) {
        throw new Error("trade in value too low");
      }

      if (vehicleOfInterestRequired || showInterestedVehicle) {
        history.push(`${history.location.pathname}/interest`);
      } else {
        history.push(`${history.location.pathname}/contact`);
      }
    } catch (err) {
      history.push(`${history.location.pathname}/no-report`);
    }
    setLoading(false);
  };

  const header = (
    <div>
      {/*Need extra div to fix an iOS issue with nested flex containers */}
      <VehicleInfoHeader
        yearId={yearId}
        makeId={makeId}
        modelId={modelId}
        trimId={trimId}
        link={`/${accountId}`}
        removeBranding={removeBranding}
      >
        <ChangeButton to={`/${accountId}`} component={Link}>
          Change Model
        </ChangeButton>
      </VehicleInfoHeader>
    </div>
  );

  return (
    <Page header={header} footer={children}>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          accessories: {},
          condition: "fairTradeInValue",
          miles: "",
          owner: "",
        }}
        validationSchema={vehicleInfoSchema}
      >
        {({ isValid }) => (
          <FormStyled>
            <SectionTitle style={{ marginTop: 20, marginBottom: 0 }}>
              Vehicle Mileage *
            </SectionTitle>
            <FieldStyled
              id="miles-input"
              name="miles"
              label="How Many Miles? *"
              component={FormattedNumberField}
              variant="outlined"
            />
            <FieldStyled
              name="condition"
              component={ConditionRadioField}
              disablePoorCondition={!showBelowAverageValue}
              contactURL={`/${params.accountId}/no-report`}
            />
            <FieldStyled name="owner" component={OwnerOptions} />
            <FieldStyled
              name="accessories"
              component={Accessories}
              trimId={trimId}
            />
            <RequiredText>*Required</RequiredText>
            <SubmitButton disabled={!isValid || loading}>NEXT</SubmitButton>
          </FormStyled>
        )}
      </Formik>
    </Page>
  );
};

export default withRouter(VehicleInfoPage);
